import { Box } from "@mui/material";
import React, { ElementType, FunctionComponent, useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { XOR } from "ts-xor";
import { Permission } from "../../api/Permissions";
import { Auth, useAuth } from "../../hooks/Auth";
import useNotifiers from "../../hooks/Notifiers";
import { AbsolutePages } from "../../constants/Sitemap";
import { AuthenticationMessages } from "../../constants/Strings";
import LoadingAnimation from "../generic/LoadingAnimation";

interface PCProps {
  component: ElementType;
}

interface PCPropsWithPermissions extends PCProps {
  requestedPermissions?: Permission | Permission[];
}

interface PCPropsWithAuthFunc extends PCProps {
  authorizeFunc?: (auth: Auth) => boolean;
}

const ProtectedContent: FunctionComponent<XOR<PCPropsWithPermissions, PCPropsWithAuthFunc>> = ({
  children,
  component,
  ...props
}) => {
  const auth = useAuth();
  const location = useLocation();
  const { notifyWarning } = useNotifiers();
  const navigate = useNavigate();
  const [accessGranted, setAccessGranted] = useState(false);

  useEffect(() => {
    if (!auth.loaded) return;

    if (auth.user == null) {
      notifyWarning(AuthenticationMessages.authenticationNeeded);
      navigate(AbsolutePages.login, {
        replace: true,
        state: { from: location },
      });
      return;
    }

    let grantAccess = false;

    if (props.authorizeFunc) {
      grantAccess = props.authorizeFunc(auth);
    } else if (props.requestedPermissions) {
      if (typeof props.requestedPermissions === "string")
        //single permission
        grantAccess = auth.hasPermission(props.requestedPermissions);
      //array
      else grantAccess = props.requestedPermissions.every((perm) => auth.hasPermission(perm));
    } else {
      grantAccess = true;
    }

    if (!grantAccess) {
      notifyWarning(AuthenticationMessages.missingPermission);
      navigate(auth.isAdmin ? AbsolutePages.admin.hub : AbsolutePages.seller.hub);
      return;
    }

    setAccessGranted(true);
  }, [auth, setAccessGranted, props, notifyWarning, navigate, location]);

  if (!accessGranted) return <LoadingAnimation />;

  const cloneWithAuth = (child: any) => React.cloneElement(child, { auth: auth });
  return React.createElement(component ?? Box, { auth: auth }, React.Children.map(children, cloneWithAuth));
};

export default ProtectedContent;
